import { createApp, Vue } from "vue";
import * as Sentry from "@sentry/vue";
import router from "./router/router";
import { getDevs } from "./developers";
import App from "./App.vue";
import ElementPlus from "element-plus";
import { i18n } from "@/i18n";
import VCalendar from "v-calendar";
import VueAwesomeSwiper from "vue-awesome-swiper";
import TheMask from "vue-the-mask";

// import VueNativeSock from "vue-native-websocket-vue3";

import "v-calendar/style.css";
import "@/assets/style/sidebar/sidebar.css";
import "@/assets/style/not-found/not-found.css";
import "@/assets/style/instructor/instructor.css";
import "@/assets/style/pagination/pagination.css";
import "@/assets/style/group-lessons/group-lessons.css";
import "@/assets/style/students-attendance/students-attendance.css";
import "@/assets/style/search-bar/search-bar.css";
import ru from "element-plus/dist/locale/ru.mjs";
import uz from "element-plus/dist/locale/uz-uz.mjs";
import en from "element-plus/dist/locale/en.mjs";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";

// Element Plus reads week start from dayjs's active locale, not the
// `first-day-of-week` prop. dayjs falls back to "en" (which has no weekStart)
// because the uz-uz locale isn't shipped in dayjs — forcing en.weekStart = 1
// makes every el-date-picker / el-calendar start on Monday.
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 1 });
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

import "@/assets/style/sales/sales.css";

import "@/assets/style/index.css";
import "element-plus/dist/index.css";

import store from "./store";

const lang = localStorage.getItem("lang") || "uz";

const locales = {
  ru,
  uz,
  en,
};

const app = createApp(App);

if (process.env.VUE_APP_SENTRY_DSN) {
  const IGNORED_ERROR_PATTERNS = [
    /Non-Error promise rejection captured/i,
    /Event `Event` \(type=error\) captured as promise rejection/i,
    /postEvent.*Method not found/i,
    /ResizeObserver loop (limit exceeded|completed with undelivered)/i,
    /Network Error/i,
    /Refresh failed/i,
    /Request aborted/i,
    /canceled|cancelled/i,
    /Loading chunk \d+ failed/i,
    // ADMIN-3C/3D/39/3B: stale index.html points at a deploy whose JS chunks
    // were already replaced — fetching them returns the SPA's 404 HTML and the
    // browser fails to parse it as JS. Cosmetic: the router.onError handler
    // below reloads on ChunkLoadError so the user lands on a fresh bundle.
    /Unexpected token '?<'?/i,
    /Failed to fetch dynamically imported module/i,
    /ChunkLoadError/i,
    /exitFullscreen.*Document not active/i,
    /SocketException: Failed host lookup/i,
    /Application shutdown failed\. Exiting/i,
    /ConnectionResetError/i,
    /A RenderFlex overflowed by/i,
    /Request failed with status code 401/i,
    // ADMIN-2J: 502 from gateway means the API was restarting / scaling.
    // Already retried at axios layer.
    /Request failed with status code 502/i,
    /null is not an object \(evaluating '.*\.checked/i,
    /Cannot read properties of null \(reading '(wrapper|offsetHeight)'\)/i,
    /^0 Error$/,
    /\btrack is not defined/i,
    /Cannot read properties of null \(reading '(style|image|length|message|removeEventListener|id|removeChild)'\)/i,
    /Cannot read properties of undefined \(reading '(length|image|id|title|includes)'\)/i,
    /null is not an object \(evaluating '.*\.(image|id|checked|length)'\)/i,
    /Cannot set properties of null \(setting 'checked'\)/i,
    /Loading CSS chunk \d+ failed/i,
  ];

  Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.1,
    environment: process.env.NODE_ENV,
    // Release tag — CI sets VUE_APP_RELEASE to the commit SHA so Glitchtip
    // can match uploaded sourcemaps to the build.
    release: process.env.VUE_APP_RELEASE,
    beforeSend(event, hint) {
      const exc = hint?.originalException;
      // Native Event objects stringify to "[object Event]" which matches no
      // pattern — always check ALL message sources, not just the first truthy.
      const excValue = event?.exception?.values?.[0]?.value || "";
      const excStr = exc && typeof exc.toString === "function" ? exc.toString() : "";
      const candidates = [exc?.message, event.message, excValue, excStr].filter(Boolean);
      const matched = candidates.some((c) =>
        IGNORED_ERROR_PATTERNS.some((re) => re.test(c))
      );
      if (matched) return null;
      const msg = candidates[0] || "";
      const hasExc = (event?.exception?.values?.length || 0) > 0;
      if (!msg && !hasExc) return null;
      const status = exc?.response?.status;
      if (status === 401 || status === 403 || status === 0) return null;
      const ua = event?.request?.headers?.["User-Agent"] || "";
      if (/bot\b|crawler|spider|headless|read-aloud|webmasters\/answer|yandex\.com\/bots|AppleWebKit.*\(compatible;/i.test(ua)) return null;
      return event;
    },
  });
}

// ADMIN-3C/3D/39/3B: when the user navigates after we deploy, the old
// index.html in their tab references chunks that no longer exist. Catch the
// resulting "Failed to fetch dynamically imported module" / "Unexpected token
// '<'" and reload once so they land on the new bundle. Suppressed in the
// Sentry ignore list above to avoid spurious alerts.
router.onError((error) => {
  const msg = (error && (error.message || error.toString())) || "";
  const looksLikeStaleChunk =
    /Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|Unexpected token '?</i.test(
      msg
    );
  if (looksLikeStaleChunk) {
    const flag = "_thesystem_reloaded_for_stale_chunk";
    if (!window.sessionStorage.getItem(flag)) {
      window.sessionStorage.setItem(flag, String(Date.now()));
      window.location.reload();
    }
  }
});

app
  .use(router)
  .use(i18n)
  .use(ElementPlus, {
    locale: locales[lang],
  })
  .use(store)
  .use(getDevs)
  .use(VCalendar, { firstDayOfWeek: 2 })
  .use(VueAwesomeSwiper)
  .use(TheMask);

// Global components used across modules
import TableResponsive from "@/components/TableResponsive.vue";
app.component("TableResponsive", TableResponsive);

// Custom directives for rich content
import { vLazyYoutube } from "@/directives/vLazyYoutube";
import { vImagePreview } from "@/directives/vImagePreview";
app.directive("lazy-youtube", vLazyYoutube);
app.directive("image-preview", vImagePreview);

// DEV-ONLY: offline Finance fixtures (see src/dev/financeMock.js). The flag is
// inlined at build time, so this whole block — and the fixtures it pulls in — is
// dropped from production builds. Installed synchronously before mount so the
// axios adapter is swapped before the first finance request fires.
if (process.env.VUE_APP_FINANCE_MOCK === "true") {
  // eslint-disable-next-line global-require
  require("@/dev/financeMock").installFinanceMock();
}

app.mount("#app");
